/**
 * Форма выбора свойства.
 *
 * Логика:
 * 1. По subjectIri загружаются rdf:type субъекта.
 * 2. По rdf:type загружаются свойства, связанные с типом.
 * 3. Дополнительно загружается общий список свойств.
 * 4. По умолчанию отображаются только свойства, связанные с типом.
 * 5. При включении "Показать все свойства" отображаются и остальные свойства.
 * 6. excludedProperties и existing исключаются из списка выбора.
 */
import React from 'react';
import { connect } from 'react-redux';
import { Form, Select, Spin, Alert, Space, Checkbox, Input, Button, Divider } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import Label from './Label';
import { OwlRepository } from '../../redux';

const RDF_TYPE = 'http://www.w3.org/1999/02/22-rdf-syntax-ns#type';
const OWL_THING = 'http://www.w3.org/2002/07/owl#Thing';

const formItemLayout = {
  labelCol  : { xs: { span: 24 }, sm: { span: 6 } },
  wrapperCol: { xs: { span: 24 }, sm: { span: 18 } },
};

const { Option, OptGroup } = Select;

const unique = (items) => Array.from(new Set((items || []).filter(Boolean)));

const normalizePropertyResponse = (response) => {
  if (!response) return [];

  const predicates = response.predicates || response.results?.bindings || response;

  if (!Array.isArray(predicates)) return [];

  return unique(
    predicates
      .map((item) => {
        if (typeof item === 'string') return item;

        return (
          item?.property?.value ||
          item?.predicate?.value ||
          item?.uri?.value ||
          item?.value ||
          item?.property ||
          item?.predicate ||
          item?.uri ||
          ''
        );
      })
      .filter(Boolean)
  );
};

const normalizeTypesResponse = (response) => {
  if (!response) return [];

  const types = response.types || response.results?.bindings || response;

  if (!Array.isArray(types)) return [];

  return unique(
    types
      .map((item) => {
        if (typeof item === 'string') return item;

        return (
          item?.type?.value ||
          item?.rdfType?.value ||
          item?.uri?.value ||
          item?.value ||
          item?.type ||
          item?.rdfType ||
          item?.uri ||
          ''
        );
      })
      .filter(Boolean)
  );
};

const getPropertyIri = (item) => {
  if (typeof item === 'string') return item;

  return (
    item?.property?.value ||
    item?.predicate?.value ||
    item?.uri?.value ||
    item?.value ||
    item?.property ||
    item?.predicate ||
    item?.uri ||
    ''
  );
};

const getPropertyDomain = (item) => {
  if (!item || typeof item === 'string') return '';

  return (
    item?.domain?.value ||
    item?.domain ||
    ''
  );
};

const hasEmptyOrThingDomain = (item) => {
  const domain = getPropertyDomain(item);

  return !domain || domain === OWL_THING;
};

const getPropertyItems = (response) => {
  if (!response) return [];

  const items = response.predicates || response.results?.bindings || response;

  return Array.isArray(items) ? items : [];
};

class SelectProperty extends React.Component {

  state = {
    loading: false,
    loaded: false,
    error: '',

    subjectIri: undefined,

    rdfTypes: [],

    typePredicates: [],
    parentPredicates: [],
    commonPredicates: [],
    allPredicates: [],

    selectedPredicate: undefined,

    showParentProperties: false,
    showCommonProperties: false,
    showAllProperties: false,

    manualPredicateIri: '',
  };
  
  //formRef = this.props.formRef || React.createRef();  

  static getDerivedStateFromProps(props, state) {
    if (props.subjectIri !== state.subjectIri) {
      return {
        subjectIri: props.subjectIri,

        loading: false,
        loaded: false,
        error: '',

        rdfTypes: [],

        typePredicates: [],
        parentPredicates: [],
        commonPredicates: [],
        allPredicates: [],

        selectedPredicate: undefined,

        showParentProperties: false,
        showCommonProperties: false,
        showAllProperties: false,

        manualPredicateIri: '',
      };
    }

    return null;
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate() {
    this.loadData();
  }

  getExcludedSet = () => {
    const {
      existing = [],
      excludedProperties = [],
    } = this.props;

    return new Set([
      ...existing,
      ...excludedProperties,
    ].filter(Boolean));
  };

  loadData() {
    if (this.state.loaded || this.state.loading) return;

    const {
      subjectIri,
      onLoadRdfTypes,
      onLoadProperties,
      onLoadAllProperties,
      factory,
    } = this.props;

    if (!subjectIri) return;

    this.setState({
      loading: true,
      error: '',
    });

    onLoadRdfTypes(subjectIri)
      .then((typesResponse) => {
        const rdfTypes = normalizeTypesResponse(typesResponse);

        const typeLoads = rdfTypes.map((rdfType) =>
          onLoadProperties(rdfType)
            .then((response) => normalizePropertyResponse(response))
            .catch(() => [])
        );

        const parentClasses = unique(
          rdfTypes
            .flatMap((rdfType) => {
              if (!factory?.provider?.getSuperClasses) return [];

              return factory.provider.getSuperClasses(rdfType, true);
            })
            .filter((item) => item !== OWL_THING)
            .filter((item) => !rdfTypes.includes(item))
        );

        const parentLoads = parentClasses.map((parentType) =>
          onLoadProperties(parentType)
            .then((response) => normalizePropertyResponse(response))
            .catch(() => [])
        );

        return Promise.all([
          Promise.all(typeLoads),
          Promise.all(parentLoads),
          onLoadAllProperties(),
        ]).then(([typePropertiesResults, parentPropertiesResults, allResponse]) => {
          const excludedSet = this.getExcludedSet();

          const typePredicatesRaw = unique(typePropertiesResults.flat());
          const parentPredicatesRaw = unique(parentPropertiesResults.flat());

          const allPropertyItems = getPropertyItems(allResponse);
          const allPredicatesRaw = normalizePropertyResponse(allResponse);

          const typePredicates = unique(
            typePredicatesRaw
              .filter((item) => !excludedSet.has(item))
          );

          const typeSet = new Set(typePredicatesRaw);

          const parentPredicates = unique(
            parentPredicatesRaw
              .filter((item) => !excludedSet.has(item))
              .filter((item) => !typeSet.has(item))
          );

          const parentSet = new Set(parentPredicatesRaw);

          const commonPredicates = unique(
            allPropertyItems
              .filter(hasEmptyOrThingDomain)
              .map(getPropertyIri)
              .filter(Boolean)
              .filter((item) => !excludedSet.has(item))
              .filter((item) => !typeSet.has(item))
              .filter((item) => !parentSet.has(item))
          );

          const commonSet = new Set(commonPredicates);

          const allPredicates = unique(
            allPredicatesRaw
              .filter((item) => !excludedSet.has(item))
              .filter((item) => !typeSet.has(item))
              .filter((item) => !parentSet.has(item))
              .filter((item) => !commonSet.has(item))
          );

          this.setState({
            loading: false,
            loaded: true,
            rdfTypes,

            typePredicates,
            parentPredicates,
            commonPredicates,
            allPredicates,

            showParentProperties: parentPredicates.length > 0
              ? this.state.showParentProperties
              : false,

            showCommonProperties: commonPredicates.length > 0
              ? this.state.showCommonProperties
              : false,

            showAllProperties: allPredicates.length > 0
              ? this.state.showAllProperties
              : false,
          });
        });
      })
      .catch((e) => {
        console.error('SelectProperty loadData Error!', e);

        this.setState({
          loading: false,
          loaded: true,
          error: e.message || 'Ошибка загрузки типов и свойств',
        });
      });
  }

  handleShowParentPropertiesChange = (event) => {
    this.setState({
      showParentProperties: event.target.checked,
    });
  };

  handleShowCommonPropertiesChange = (event) => {
    this.setState({
      showCommonProperties: event.target.checked,
    });
  };

  handleShowAllPropertiesChange = (event) => {
    this.setState({
      showAllProperties: event.target.checked,
    });
  };

  handlePredicateChange = (predicateIri) => {
    this.setState({
      selectedPredicate: predicateIri,
    });

    this.props.formRef.current?.setFieldsValue({
      predicateIri,
    });

    this.props.onSelectionChange?.(predicateIri);
  };

  handleShowAllPropertiesChange = (event) => {
    this.setState({
      showAllProperties: event.target.checked,
    });
  };

  handleFinish = (values) => {
    const { subjectIri, onFinish } = this.props;

    onFinish?.({
      subjectIri,
      predicateIri: values.predicateIri,
    });
  };

  renderPredicateOption = (item) => (
    <Option key={item} value={item}>
      <Label iri={item} /> ({item})
    </Option>
  );

  handleManualPredicateChange = (event) => {
    this.setState({
      manualPredicateIri: event.target.value,
    });
  };

  normalizeManualPredicateIri = (value) => {
    if (!value) return '';

    const iri = value.trim();

    if (!iri) return '';

    return iri;
  };

  addManualPredicate = (event) => {
    event.preventDefault();

    const predicateIri = this.normalizeManualPredicateIri(
      this.state.manualPredicateIri
    );

    if (!predicateIri) return;

    const excludedSet = this.getExcludedSet();

    if (excludedSet.has(predicateIri)) {
      this.setState({
        error: 'Это свойство уже есть в карточке или исключено из выбора',
      });
      return;
    }

    const visiblePredicates = [
      ...this.state.typePredicates,
      ...this.state.parentPredicates,
      ...this.state.commonPredicates,
      ...this.state.allPredicates,
    ];

    const nextState = {
      selectedPredicate: predicateIri,
      manualPredicateIri: '',
      error: '',
    };

    if (!visiblePredicates.includes(predicateIri)) {
      nextState.allPredicates = unique([
        ...this.state.allPredicates,
        predicateIri,
      ]);
      nextState.showAllProperties = true;
    }

    this.setState(nextState);

    this.props.formRef.current?.setFieldsValue({
      predicateIri,
    });

    this.props.onSelectionChange?.(predicateIri);
  };

  render() {
    const { subjectIri } = this.props;

    const {
      loading,
      error,
      rdfTypes,

      typePredicates,
      parentPredicates,
      commonPredicates,
      allPredicates,

      showParentProperties,
      showCommonProperties,
      showAllProperties,
    } = this.state;

    return (
      <Spin spinning={loading} tip="Загрузка типов и свойств...">
        <Form
          ref={this.props.formRef}
          {...formItemLayout}
          className="ant-edit-form"
          onFinish={this.handleFinish}
        >
          <Form.Item label="Субъект">
            <span style={{ wordBreak: 'break-all' }}>
              {subjectIri}
            </span>
          </Form.Item>

          {rdfTypes.length > 0 && (
            <Form.Item label={<Label iri={RDF_TYPE} />}>
              <Space direction="vertical" size={2}>
                {rdfTypes.map((type) => (
                  <span key={type} style={{ wordBreak: 'break-all' }}>
                    {type}
                  </span>
                ))}
              </Space>
            </Form.Item>
          )}

          {error && (
            <Form.Item
              wrapperCol={{
                xs: { span: 24 },
                sm: { offset: 6, span: 18 },
              }}
            >
              <Alert type="error" showIcon message={error} />
            </Form.Item>
          )}

          <Form.Item
            label="Свойство"
            name="predicateIri"
            rules={[
              {
                required: true,
                message: 'Пожалуйста, выберите свойство!',
              },
            ]}
          >



          <Select
            showSearch
            placeholder="Выберите свойство"
            onChange={this.handlePredicateChange}
            optionFilterProp="children"
            dropdownRender={(menu) => (
              <>
                {menu}

                <Divider style={{ margin: '8px 0' }} />

                <Space style={{ padding: '0 8px 4px', width: '100%' }}>
                  <Input
                    placeholder="Укажите IRI свойства"
                    value={this.state.manualPredicateIri}
                    onChange={this.handleManualPredicateChange}
                    onPressEnter={this.addManualPredicate}
                  />

                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={this.addManualPredicate}
                    disabled={!this.state.manualPredicateIri?.trim()}
                  >
                    Добавить
                  </Button>
                </Space>
              </>
            )}
          >
            {typePredicates.length > 0 && (
              <OptGroup label="Свойства текущего типа">
                {typePredicates.map(this.renderPredicateOption)}
              </OptGroup>
            )}

            {showParentProperties && parentPredicates.length > 0 && (
              <OptGroup label="Свойства родительских классов">
                {parentPredicates.map(this.renderPredicateOption)}
              </OptGroup>
            )}

            {showCommonProperties && commonPredicates.length > 0 && (
              <OptGroup label="Общие свойства">
                {commonPredicates.map(this.renderPredicateOption)}
              </OptGroup>
            )}

            {showAllProperties && allPredicates.length > 0 && (
              <OptGroup label="Все остальные свойства">
                {allPredicates.map(this.renderPredicateOption)}
              </OptGroup>
            )}
          </Select>

            
          </Form.Item>

          <Form.Item
            wrapperCol={{
              xs: { span: 24 },
              sm: { offset: 6, span: 18 },
            }}
          >
            <Space direction="vertical">
              <Checkbox
                checked={showParentProperties && parentPredicates.length > 0}
                disabled={parentPredicates.length === 0}
                onChange={this.handleShowParentPropertiesChange}
              >
                Показывать свойства родителей
              </Checkbox>

              <Checkbox
                checked={showCommonProperties && commonPredicates.length > 0}
                disabled={commonPredicates.length === 0}
                onChange={this.handleShowCommonPropertiesChange}
              >
                Показать общие свойства
              </Checkbox>

              <Checkbox
                checked={showAllProperties && allPredicates.length > 0}
                disabled={allPredicates.length === 0}
                onChange={this.handleShowAllPropertiesChange}
              >
                Показать все свойства
              </Checkbox>
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    );
  }
}

function mapStateToProps(state, props) {
  const { vset } = state;
  const { graph, lang } = props;

  return {
    graph: graph ? graph : vset.graph,
    lang: lang ? lang : vset.lang,
    vset,
  };
}

export default connect(
  mapStateToProps,
  (dispatch) => ({
    onLoadRdfTypes(subjectIri) {
      return dispatch(OwlRepository.loadRdfTypes(subjectIri));
    },

    onLoadProperties(rdfType) {
      return dispatch(OwlRepository.loadDomain(rdfType));
    },

    onLoadAllProperties() {
      return dispatch(OwlRepository.loadAllProperties());
    },
  })
)(SelectProperty);